import { injectable } from "inversify";
import { makeAutoObservable, runInAction } from "mobx";
import type {
  DemoSignalCardData,
  IDemoSignalsManager,
} from "@/lib/types/demo-session";

/** Cards persist for the session; connectionLost proxies online/offline since AvaturnHead exposes no reconnect signal. */
@injectable()
export class DemoSignalsStore implements IDemoSignalsManager {
  cards: DemoSignalCardData[] = [];
  connectionLost: boolean = false;
  private dismissedKeys = new Set<string>();
  private listening = false;

  private handleOffline = (): void => {
    runInAction(() => {
      this.connectionLost = true;
    });
  };
  private handleOnline = (): void => {
    runInAction(() => {
      this.connectionLost = false;
    });
  };

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  watchConnection(): void {
    if (this.listening || typeof window === "undefined") return;
    this.listening = true;
    window.addEventListener("offline", this.handleOffline);
    window.addEventListener("online", this.handleOnline);
  }

  unwatchConnection(): void {
    if (!this.listening) return;
    this.listening = false;
    window.removeEventListener("offline", this.handleOffline);
    window.removeEventListener("online", this.handleOnline);
  }

  push(card: DemoSignalCardData): void {
    runInAction(() => {
      const existing = this.cards.findIndex(
        (c) => c.signalKey === card.signalKey,
      );
      if (existing !== -1) {
        this.cards[existing] = card;
        return;
      }
      this.cards.unshift(card);
    });
  }

  /** Merges in the recap's fired-so-far signals rather than replacing the
   * board — a live `demo_signal` can arrive before /recap reflects it, and a
   * naive replace would make that card flicker away. */
  hydrate(cards: DemoSignalCardData[]): void {
    runInAction(() => {
      const bySignalKey = new Map(this.cards.map((c) => [c.signalKey, c]));
      for (const card of cards) {
        if (!bySignalKey.has(card.signalKey)) {
          bySignalKey.set(card.signalKey, card);
        }
      }
      this.cards = Array.from(bySignalKey.values()).sort(
        (a, b) => new Date(b.firedAt).getTime() - new Date(a.firedAt).getTime(),
      );
    });
  }

  dismiss(signalKey: string): void {
    runInAction(() => {
      this.dismissedKeys.add(signalKey);
    });
  }

  isDismissed(signalKey: string): boolean {
    return this.dismissedKeys.has(signalKey);
  }

  reset(): void {
    this.unwatchConnection();
    runInAction(() => {
      this.cards = [];
      this.connectionLost = false;
      this.dismissedKeys = new Set();
    });
  }
}
